@file:OptIn(ExperimentalWasmJsInterop::class)

import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.drop
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.launch
import com.bikompanion.ui.home.HomeScreenActions
import com.bikompanion.ui.home.HomeViewModel
import com.bikompanion.ui.home.StationFocusTarget
import com.bikompanion.ui.link.StationLinks

/** The page's own address without any query: what a station link is built on. */
val browserBaseUrl: String
    get() = window.location.origin + window.location.pathname

/**
 * Keeps the address bar and tab title saying the same thing as the open station sheet.
 *
 * Opening a station pushes `?station=<network>/<id>` so a refresh, a bookmark or a pasted link
 * lands on it, and the browser's Back button closes the sheet. The view model is the only owner
 * of what is selected: this only mirrors it outwards and feeds history steps back in, and each
 * direction first checks the address already matches so the two never echo.
 */
class BrowserStationHistory(
    private val viewModel: HomeViewModel,
    private val scope: CoroutineScope,
) {
    /** Taken from the page rather than repeated here, so `index.html` stays its one source. */
    private val brandTitle = document.title

    fun start() {
        window.addEventListener("popstate", { onHistoryStep() })
        scope.launch {
            // Not the first value: before a link has been applied nothing is selected, and
            // mirroring that would erase the very address being opened.
            viewModel.stateFlow
                .map { it.selectedStationTarget to pageTitle(it) }
                .distinctUntilChanged()
                .drop(1)
                .collect { (target, title) ->
                    // In this order: browsers label a history entry with the title it has while
                    // current, so renaming first would put the station's name on the map's entry.
                    mirror(target)
                    document.title = title
                }
        }
    }

    /**
     * The most specific words first and the brand last, as search results and narrow tabs cut
     * from the end. Resolved from the loaded stations because a link selects before they arrive.
     */
    private fun pageTitle(state: HomeViewModel.State): String {
        val target = state.selectedStationTarget ?: return brandTitle
        val station = state.stationsByNetwork[target.networkId]
            ?.firstOrNull { it.id == target.stationId }
            ?: return brandTitle
        val network = state.networks.firstOrNull { it.id == target.networkId }
            ?: return "${station.name} | $brandTitle"
        return "${station.name} – ${network.name} | $brandTitle"
    }

    /** Opens whatever the address names, for the first load. */
    fun openCurrentAddress() {
        StationLinks.parse(window.location.search)?.let { viewModel.onAction(HomeScreenActions.StationLinkOpened(it)) }
    }

    private fun mirror(target: StationFocusTarget?) {
        val shown = StationLinks.parse(window.location.search)
        if (shown == target) return
        val history = window.history
        when {
            target == null && history.state != null -> {
                // This entry was pushed for the sheet, so stepping back is what removes it and
                // leaves Back and the sheet's own close button agreeing on one history.
                history.back()
            }

            target == null -> {
                history.replaceState(null, "", browserBaseUrl)
            }

            shown == null -> {
                history.pushState(PushedEntry, "", StationLinks.url(browserBaseUrl, target))
            }

            // Station to station is a swap, not a step: Back should leave the sheet, not walk
            // through every marker that was tapped. The entry keeps whatever marker it had: one
            // opened from a pasted link was not pushed for the sheet, so closing must not step
            // back over it and out of the app.
            else -> {
                history.replaceState(history.state, "", StationLinks.url(browserBaseUrl, target))
            }
        }
    }

    private fun onHistoryStep() {
        val target = StationLinks.parse(window.location.search)
        when {
            target == null -> viewModel.onAction(HomeScreenActions.StationDismissed)
            target != viewModel.stateFlow.value.selectedStationTarget -> viewModel.onAction(HomeScreenActions.StationLinkOpened(target))
        }
    }
}

/** Marks a history entry as ours, so closing the sheet knows it can step back over it. */
private val PushedEntry: JsAny = js("({ bikompanion: true })")

/**
 * Hands [url] to the browser's share sheet. False when the browser has none (Firefox on desktop),
 * so the caller can copy the link instead.
 */
fun shareInBrowser(
    title: String,
    url: String,
): Boolean {
    val navigator = window.navigator.asDynamic()
    if (jsTypeOf(navigator.share) != "function") return false
    val data: dynamic = js("({})")
    data.title = title
    data.url = url
    // Closing the share sheet rejects the promise with an AbortError; that is a choice, not a failure.
    navigator.share(data).catch { _: dynamic -> null }
    return true
}
